import React from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const img = (p) => `${BACKEND}${p}`;

/**
 * Editorial storytelling strip — "The Craft".
 * Asymmetric three-up layout with oversized italic indices,
 * a feature image, and copper hairline rhythm.
 */
const MaterialsStrip = () => {
  const notes = [
    {
      n: "I",
      title: "Material",
      copy: "Every piece is printed in PLA, a plant-based bioplastic chosen for its warmth and its hand-finished feel.",
    },
    {
      n: "II",
      title: "Form",
      copy: "Parametric geometries sculpted over weeks — each lamp catches light in a way no two will repeat.",
    },
    {
      n: "III",
      title: "Finish",
      copy: "Sanded, sealed and inspected in-studio. Imperfections are left where they tell the piece's story.",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-[var(--ivory-soft)] vc-grain" data-testid="materials-strip">
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-20 py-28 lg:py-40 z-[2]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
          {/* Image column — feature */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 vc-fade-up">
            <div className="relative vc-copper-halo">
              <div className="aspect-[4/5] overflow-hidden bg-[var(--sand)] vc-img-zoom">
                <img
                  src={img("/api/static/products/studio-02.webp")}
                  srcSet={`${img("/api/static/products/studio-02-480.webp")} 480w, ${img("/api/static/products/studio-02.webp")} 1050w`}
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  alt="Inside the studio — the craft of sculpted light"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 left-6 bg-[var(--espresso)] text-[var(--cream)] px-6 py-3 text-[10px] tracking-[0.35em] uppercase">
                Inside the studio
              </div>
            </div>
          </div>

          {/* Copy column — three notes */}
          <div className="lg:col-span-7">
            <p className="vc-eyebrow text-[var(--copper)] mb-10" data-testid="materials-eyebrow">
              The Craft
            </p>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.08] text-[var(--espresso)] max-w-xl vc-fade-up vc-stagger-1">
              A quiet process. <em className="italic text-[var(--copper)]">Carefully kept.</em>
            </h2>
            <p className="mt-8 text-[var(--espresso)]/65 leading-[1.9] max-w-md vc-fade-up vc-stagger-2">
              Every piece passes through three unhurried hands — design, print, finish. Nothing is rushed; nothing is identical.
            </p>

            <div className="mt-16 space-y-14">
              {notes.map((note, i) => (
                <div
                  key={note.n}
                  className={`grid grid-cols-12 gap-6 items-start vc-fade-up vc-stagger-${i + 2}`}
                  data-testid={`materials-note-${note.n.toLowerCase()}`}
                >
                  <div className="col-span-2 lg:col-span-1">
                    <span className="vc-index block">{note.n}</span>
                  </div>
                  <div className="col-span-10 lg:col-span-11 pt-2">
                    <div className="vc-divider mb-6" />
                    <h3 className="font-serif-display font-light text-2xl lg:text-3xl text-[var(--espresso)] leading-tight">
                      {note.title}
                    </h3>
                    <p className="mt-4 text-[var(--espresso)]/65 leading-[1.9] max-w-lg tracking-wide">
                      {note.copy}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MaterialsStrip;
